// src/pages/shipping/QuoteSimulator.tsx
/* Movatic WMS — Frontend | CesarFilho 932048016
   Simulador de cotação simples (peso/volume/destino).
   Usa GET /api/shipping/quote/?carrier=&table=&weight_kg=&volume_m3=&dest=
*/
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Input, Label } from "@/components/ui";

type Carrier = { id: number; name: string; code?: string };
type RateTable = { id: number; name: string; carrier: number };
type QuoteResult = {
    ok?: boolean;
    price?: string;
    currency?: string;
    transit_days?: number | null;
    table_id?: number;
    carrier_id?: number;
    breakdown?: any;
    detail?: string;
};

export default function QuoteSimulator() {
    const [carrier, setCarrier] = useState<number | "">("");
    const [table, setTable] = useState<number | "">("");
    const [weight, setWeight] = useState<string>("");
    const [volume, setVolume] = useState<string>("");
    const [dest, setDest] = useState<string>("");

    const qCarriers = useQuery<Carrier[]>({
        queryKey: ["carriers"],
        queryFn: async () => {
            const { data } = await api.get("/api/partners/carriers/", { params: { page_size: 1000 } });
            if (Array.isArray(data)) return data as Carrier[];
            return (data?.results ?? []) as Carrier[];
        },
        staleTime: 60_000,
        // evita TS7006: param tipado
        placeholderData: (p: Carrier[] | undefined) => p,
    });

    const qTables = useQuery<RateTable[]>({
        queryKey: ["rate-tables-all", { carrier }],
        queryFn: async () => {
            const { data } = await api.get("/api/shipping/rate-tables/", {
                params: { page_size: 1000, carrier: carrier || undefined },
            });
            if (Array.isArray(data)) return data as RateTable[];
            return (data?.results ?? []) as RateTable[];
        },
        enabled: !!carrier || carrier === "",
        // evita TS7006: param tipado
        placeholderData: (p: RateTable[] | undefined) => p,
        staleTime: 10_000,
    });

    const [result, setResult] = useState<QuoteResult | null>(null);
    const [loading, setLoading] = useState(false);

    const doQuote = async () => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.get<QuoteResult>("/api/shipping/quote/", {
                params: {
                    carrier: carrier || undefined,
                    table: table || undefined,
                    weight_kg: weight || undefined,
                    volume_m3: volume || undefined,
                    dest: dest || undefined,
                },
            });
            setResult(data);
        } catch (e: any) {
            setResult({ ok: false, detail: String(e?.message || e) });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // se trocar carrier e a tabela atual não pertence a ele, limpa
        if (table && qTables.data && !qTables.data.find((t: RateTable) => t.id === table)) {
            setTable("");
        }
    }, [carrier, table, qTables.data]);

    return (
        <div className="p-4 max-w-5xl mx-auto space-y-4">
            <h1 className="text-xl font-semibold">Simulador de Frete</h1>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="md:col-span-2">
                    <Label>Transportadora</Label>
                    <select
                        className="block w-full rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                        value={carrier as any}
                        onChange={(e) => setCarrier(e.target.value ? Number(e.target.value) : "")}
                    >
                        <option value="">(Melhor preço entre todas)</option>
                        {(qCarriers.data ?? []).map((c: Carrier) => (
                            <option key={c.id} value={c.id}>
                                {c.code ? `${c.code} — ${c.name}` : c.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-2">
                    <Label>Tabela (opcional)</Label>
                    <select
                        className="block w-full rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                        value={table as any}
                        onChange={(e) => setTable(e.target.value ? Number(e.target.value) : "")}
                    >
                        <option value="">(Auto)</option>
                        {(qTables.data ?? []).map((t: RateTable) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <Label>Peso (kg)</Label>
                    <Input value={weight} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWeight(e.target.value)} />
                </div>
                <div>
                    <Label>Volume (m³)</Label>
                    <Input value={volume} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVolume(e.target.value)} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                    <Label>Destino (CEP/Cidade/UF/País)</Label>
                    <Input
                        value={dest}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDest(e.target.value)}
                        placeholder="ex.: 4750-123, Barcelos, PT"
                    />
                </div>
                <div className="flex items-end">
                    <Button onClick={doQuote} disabled={loading}>
                        {loading ? "Calculando..." : "Simular"}
                    </Button>
                </div>
            </div>

            {result && (
                <div className="border rounded p-3">
                    {result.ok === false ? (
                        <div className="text-red-400">Falha: {result.detail ?? "erro"}</div>
                    ) : (
                        <div className="space-y-2">
                            <div className="text-lg">
                                Preço: <b>{result.price ?? "—"}</b> {result.currency ?? ""}
                            </div>
                            <div>Transit: {result.transit_days ?? "—"} dia(s)</div>
                            <div>
                                Tabela: {result.table_id ?? "—"} • Transportadora: {result.carrier_id ?? "—"}
                            </div>
                            <pre className="mt-3 text-xs bg-slate-900/40 border rounded p-2 overflow-auto">
                                {JSON.stringify(result, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
